/**
 * useQuickAnswerChat — message state for the Robi Quick Answer conversation.
 *
 * The WlbHelpAgent conversation is persistent server-side, so the stored
 * history is loaded whenever the surface opens. Robi's reply to a new message
 * arrives in the SendMessage response rather than through a refetch.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import conversationApi from '../api/conversation/conversationApi';
import {
  generateLocalMessageId,
  mapMessageDto,
} from '../api/conversation/conversationMapper';
import messageService from '../api/services/messageService';
import {
  ensureReplyAfter,
  mapQuickAnswerAiMessage,
  parseSendMessageResponse,
  type QuickAnswerAgent,
} from '../api/conversation/quickAnswerMessages';
import { ChatContentType } from '../constants';
import { useProfile } from '../context/ProfileContext';
import { useTranslation } from '../i18n/useTranslation';
import { ToastService } from '../platform/notifier';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import type { ApiError } from '../types/api';
import type { ExtendedChatMessage } from '../types/chat';

export interface UseQuickAnswerChatOptions {
  conversationId: number | null;
  agent: QuickAnswerAgent;
}

export interface UseQuickAnswerChatResult {
  /** Newest first, matching every other chat surface in the app. */
  messages: ExtendedChatMessage[];
  currentUser: { _id: string | number; name: string };
  /** True while the stored history is being fetched. */
  loadingHistory: boolean;
  sending: boolean;
  /** Resolves false when the send failed, so callers can restore the input. */
  send: (text: string) => Promise<boolean>;
  reset: () => void;
}

const HISTORY_PAGE_SIZE = 30;

const useQuickAnswerChat = ({
  conversationId,
  agent,
}: UseQuickAnswerChatOptions): UseQuickAnswerChatResult => {
  const { profile } = useProfile();
  const { t } = useTranslation();
  const [messages, setMessages] = useState<ExtendedChatMessage[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sending, setSending] = useState(false);

  const currentUserId = profile?.userId;

  const currentUser = useMemo(
    () => ({
      _id: currentUserId ?? 'user-1',
      name: profile?.userName?.trim() || t('chat.me'),
      username: profile?.userName?.trim() || null,
    }),
    [currentUserId, profile?.userName, t],
  );

  useEffect(() => {
    if (!conversationId) {
      setMessages([]);
      return;
    }

    let cancelled = false;
    setLoadingHistory(true);

    messageService
      .getMessages({
        conversationId,
        page: 1,
        pageSize: HISTORY_PAGE_SIZE,
        currentUserId,
      })
      .then(result => {
        if (cancelled) return;
        // Keep anything sent while the fetch was in flight above the history.
        setMessages(prev => {
          const restored = result.messages ?? [];
          const known = new Set(restored.map(m => String(m._id)));
          const pendingLocal = prev.filter(m => !known.has(String(m._id)));
          return [...pendingLocal, ...restored];
        });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        ToastService.show({
          type: 'error',
          message: getApiErrorMessage(err as ApiError),
        });
      })
      .finally(() => {
        if (!cancelled) setLoadingHistory(false);
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, currentUserId]);

  const agentId = agent._id;
  const agentName = agent.name;
  const agentAvatar = agent.avatar ?? null;

  const send = useCallback(
    async (text: string): Promise<boolean> => {
      const trimmed = text.trim();
      if (!trimmed || !conversationId) return false;

      const optimisticId = generateLocalMessageId();
      setMessages(prev => [
        {
          _id: optimisticId,
          text: trimmed,
          createdAt: new Date(),
          user: currentUser,
          pending: true,
        },
        ...prev,
      ]);
      setSending(true);

      try {
        const response = await conversationApi.sendMessage(conversationId, {
          bodyText: trimmed,
          messageTypeId: ChatContentType.Text,
          replyToMessageId: null,
          attachments: [],
        });
        const { userMessage, aiMessage } = parseSendMessageResponse(
          response.data,
        );

        setMessages(prev => {
          const withoutOptimistic = prev.filter(m => m._id !== optimisticId);
          const sentUser = userMessage
            ? mapMessageDto(userMessage, currentUserId)
            : prev.find(m => m._id === optimisticId);
          const confirmedUser = sentUser
            ? { ...sentUser, text: sentUser.text || trimmed }
            : undefined;

          const next: ExtendedChatMessage[] = [];
          if (aiMessage?.bodyText?.trim()) {
            next.push(
              ensureReplyAfter(
                mapQuickAnswerAiMessage(aiMessage, {
                  _id: agentId,
                  name: agentName,
                  avatar: agentAvatar,
                }),
                confirmedUser,
              ),
            );
          }
          if (confirmedUser) next.push(confirmedUser);

          return [...next, ...withoutOptimistic];
        });
        return true;
      } catch (err: unknown) {
        ToastService.show({
          type: 'error',
          message: getApiErrorMessage(err as ApiError),
        });
        setMessages(prev => prev.filter(m => m._id !== optimisticId));
        return false;
      } finally {
        setSending(false);
      }
    },
    [conversationId, currentUser, currentUserId, agentId, agentName, agentAvatar],
  );

  const reset = useCallback(() => {
    setMessages([]);
    setSending(false);
  }, []);

  return { messages, currentUser, loadingHistory, sending, send, reset };
};

export default useQuickAnswerChat;
